import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import apiClient from "../../services/api";

const FONT = "'Inter', sans-serif";

const KPI_COLOR: Record<string, string> = {
  IN_PROGRESS: "#22C55E",
  PENDING:     "#9CA3AF",
  NOT_STARTED: "#9CA3AF",
  AT_RISK:     "#F97316",
  DELAYED:     "#EF4444",
  BLOCKED:     "#7C3AED",
};

interface SupplierRow {
  supplier_name: string;
  total: number;
  [group: string]: number | string;
}

interface SupplierBreakdown {
  groups: string[];
  suppliers: SupplierRow[];
}

const fmtAxis = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
};

const shortLabel = (s: string): string => {
  const key = s.toUpperCase().trim().replaceAll(/[\s_]+/g, "_");
  if (key === "NOT_STARTED" || key === "PENDING") return "Pending";
  return s.replaceAll("_", " ").toLowerCase().replaceAll(/\b\w/g, (c) => c.toUpperCase());
};

function SupplierYTick({ x, y, payload }: Readonly<{ x?: number; y?: number; payload?: { value: string } }>) {
  const name = payload?.value ?? "";
  const display = name.length > 28 ? name.slice(0, 27) + "\u2026" : name;
  return (
    <text x={x} y={y} dy={4} textAnchor="end" fontFamily={FONT} fontSize={11} fill="#6B7280">
      {display}
    </text>
  );
}

const TopSuppliersByValue: React.FC = () => {
  const [data, setData] = useState<SupplierBreakdown>({ groups: [], suppliers: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiClient
      .get<SupplierBreakdown>("/api/databricks/dashboard/suppliers-by-group")
      .then((res) => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const activeGroups = data.groups.filter((g) => g.toUpperCase() !== "COMPLETED");

  const top5 = [...data.suppliers]
    .sort(
      (a, b) =>
        activeGroups.reduce((s, g) => s + (Number(b[g]) || 0), 0) -
        activeGroups.reduce((s, g) => s + (Number(a[g]) || 0), 0)
    )
    .slice(0, 5);

  const maxTotal = top5.length
    ? Math.max(...top5.map((r) => activeGroups.reduce((s, g) => s + (Number(r[g]) || 0), 0)))
    : 0;

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: "12px",
        border: "1px solid #EAEAEA",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        p: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT,
          fontWeight: 600,
          fontSize: "1rem",
          color: "#111111",
          mb: 3,
        }}
      >
        Top 5 Suppliers by Shipment Value
      </Typography>

      {loading && (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={28} sx={{ color: "#22C55E" }} />
        </Box>
      )}

      {!loading && (error || top5.length === 0) && (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontFamily: FONT, fontSize: "0.875rem", color: "#9CA3AF" }}>
            No data available
          </Typography>
        </Box>
      )}

      {!loading && !error && top5.length > 0 && (
        <>
          <Box sx={{ height: 280, minHeight: 220 }}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={top5}
                layout="vertical"
                margin={{ top: 0, right: 20, bottom: 4, left: 4 }}
                barCategoryGap="25%"
              >
                <XAxis
                  type="number"
                  domain={[0, Math.ceil(maxTotal * 1.1)]}
                  tickFormatter={fmtAxis}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontFamily: FONT, fontSize: 11, fill: "#9CA3AF" }}
                  tickCount={4}
                />
                <YAxis
                  type="category"
                  dataKey="supplier_name"
                  width={180}
                  axisLine={false}
                  tickLine={false}
                  tick={<SupplierYTick />}
                />
                <Tooltip
                  formatter={(value: number, name: string) => [fmtAxis(value), shortLabel(name)]}
                  contentStyle={{
                    fontFamily: FONT,
                    fontSize: "0.8125rem",
                    borderRadius: 8,
                    border: "1px solid #EAEAEA",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  }}
                />
                {activeGroups.map((g) => (
                  <Bar
                    key={g}
                    dataKey={g}
                    name={g}
                    stackId="a"
                    fill={KPI_COLOR[g.toUpperCase()] ?? "#94A3B8"}
                    radius={0}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>

          {/* Legend */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, pt: 1.5, borderTop: "1px solid #EAEAEA" }}>
            {activeGroups.map((g) => (
              <Box key={g} sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: KPI_COLOR[g.toUpperCase()] ?? "#94A3B8", flexShrink: 0 }} />
                <Typography sx={{ fontFamily: FONT, fontSize: "0.6875rem", color: "#6B7280" }}>
                  {shortLabel(g)}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default TopSuppliersByValue;
